import { useSDK } from "~hooks";
import { Order } from "~graphql/sdk";
import { useEffect } from "react";
import {
  broadcastParamsFromReportingString,
  BroadcastReportingTracking,
  BroadcastReportingTrackingRaw,
  getBroadcastParamsFromQuery,
} from "~hooks/broadcastHelper";
import { useRouter } from "next/router";
import { addDays } from "date-fns";

const BroadcastReportingData = "BroadcastReportingData";
type BroadcastReportingDataType = BroadcastReportingTrackingRaw & {
  expiresAt: number;
};
const getBroadcastReportingTrackingCode = (): BroadcastReportingTracking | void => {
  const reportingData = localStorage?.getItem(BroadcastReportingData);
  const data: BroadcastReportingDataType | null = reportingData
    ? JSON.parse(reportingData)
    : null;

  if (data) {
    if (Date.now() > data.expiresAt) {
      localStorage?.removeItem(BroadcastReportingData);
    } else {
      return broadcastParamsFromReportingString(data.rawData);
    }
  }
};

const setReportingData = (rawData: string) => {
  localStorage?.setItem(
    BroadcastReportingData,
    JSON.stringify({
      rawData,
      expiresAt: addDays(new Date(), 7).getTime(),
    })
  );
};

export const useCampaignReportingOpen = (trigger: boolean) => {
  const router = useRouter();
  const sdk = useSDK();
  const { query } = router;
  const broadcast = getBroadcastParamsFromQuery(query);
  if (!broadcast) {
    return;
  }
  const {
    broadcastId,
    userId,
    waitlistUserId,
    membershipId,
    eventId,
    rawData,
  } = broadcast;

  useEffect(() => {
    if (trigger) {
      if (broadcastId && (userId || waitlistUserId)) {
        const doAsyncReporting = async () => {
          try {
            await sdk.broadcastReportingLinkOpened({
              input: {
                broadcastId,
                membershipId,
                eventId,
                userId,
                waitlistUserId,
              },
            });
          } catch (error) {
            // Handle errors
            console.error("Error saving data:", error);
          }
        };

        setReportingData(rawData as string);
        void doAsyncReporting();
      }
    }
  }, [trigger]);
};

export const useCampaignReportingForOrder = (order: Order) => {
  const trackingCode = getBroadcastReportingTrackingCode();
  const sdk = useSDK();
  if (!trackingCode) {
    return;
  }

  const {
    broadcastId,
    userId,
    waitlistUserId,
    eventId,
    membershipId,
  } = trackingCode;

  useEffect(() => {
    void sdk.broadcastReportingOrderCreated({
      input: {
        broadcastId,
        eventId,
        membershipId,
        userId,
        waitlistUserId,
        orderId: order.id,
      },
    });
  }, [order.id]);
};
